import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { SubscriptionService } from '../../core/subscription/subscription.service';
import { Subject, takeUntil } from 'rxjs';

export enum CouponAction {
  Collapsed = 'collapsed',
  Expanded = 'expanded',
  Added = 'added'
}

export enum CouponCodeMode {
  Input = 'input',
  Apply = 'apply',
  Applied = 'applied'
}

export enum CouponDisplayStyle {
  Detailed = 'detailed',
  Default = 'default'
}

@Component({
  selector: 'rosie-coupon-code',
  templateUrl: './coupon-code.component.html',
  styleUrls: ['./coupon-code.component.scss'],
  standalone: false
})
export class CouponCodeComponent implements OnInit, OnDestroy {
  @Input() control: FormControl;
  @Input() displayStyle: CouponDisplayStyle = CouponDisplayStyle.Default;
  @Output() onCouponApplied = new EventEmitter<void>();
  @Output() onCouponRemoved = new EventEmitter<void>();

  CouponAction = CouponAction;
  couponCodeControl = new FormControl('', [Validators.required]);
  currentState: CouponAction = CouponAction.Collapsed;
  mode: CouponCodeMode = CouponCodeMode.Apply;
  inProgress = false;
  errorMessage = '';
  appliedCouponCode = '';
  appliedCouponName = '';
  appliedCoupon: any = null;
  private destroy$ = new Subject<void>();

  constructor(public subscriptionService: SubscriptionService) { }

  ngOnInit(): void {
    this.determineMode();
    this.subscribeToSubscriptionChanges();
    this.subscribeToCouponChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get subscriptionId(): string {
    return this.subscriptionService.subscriptionData?.id;
  }

  private determineMode(): void {
    if (!this.subscriptionId) {
      this.mode = CouponCodeMode.Input;
      return;
    }

    // Check if there's already an applied coupon in the service
    if (this.subscriptionService.appliedCoupon) {
      this.appliedCoupon = this.subscriptionService.appliedCoupon;
      this.appliedCouponCode = this.subscriptionService.appliedCoupon.id;
      this.appliedCouponName = this.subscriptionService.appliedCoupon.name || this.subscriptionService.appliedCoupon.id;
      this.mode = CouponCodeMode.Applied;
      this.currentState = CouponAction.Added;
      return;
    }

    const details = this.subscriptionService.subscriptionDetails;
    if (details) {
      this.updateModeFromDetails(details);
    } else {
      this.loadSubscriptionDetails();
    }
  }

  private loadSubscriptionDetails(): void {
    this.subscriptionService.getSubscriptionDetails(this.subscriptionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (details) => {
          this.updateModeFromDetails(details);
        },
        error: () => {
          this.mode = CouponCodeMode.Apply;
        }
      });
  }

  private updateModeFromDetails(details: any): void {
    const currentCoupons = details?.currentPlan?.couponsApplied || [];
    const upcomingCoupons = details?.upcomingPlan?.couponsApplied || [];

    const coupon = currentCoupons[0] || upcomingCoupons[0] || null;

    if (coupon) {
      this.subscriptionService.appliedCoupon = coupon;
      this.appliedCoupon = coupon;
      this.appliedCouponCode = coupon.id;
      this.appliedCouponName = coupon.name || coupon.id;
      this.mode = CouponCodeMode.Applied;
      this.currentState = CouponAction.Added;
    } else {
      this.subscriptionService.appliedCoupon = null;
      this.mode = CouponCodeMode.Apply;
    }
  }

  private subscribeToSubscriptionChanges(): void {
    if (!this.subscriptionId) {
      return;
    }

    this.subscriptionService.created
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const details = this.subscriptionService.subscriptionDetails;
        if (details) {
          this.updateModeFromDetails(details);
        }
      });
  }

  private subscribeToCouponChanges(): void {
    this.subscriptionService.couponChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe((couponCode: string) => {
        if (couponCode) {
          this.appliedCouponCode = couponCode;
          this.appliedCoupon = this.subscriptionService.appliedCoupon;
          this.appliedCouponName = this.appliedCoupon?.name || couponCode;
          this.mode = CouponCodeMode.Applied;
          this.currentState = CouponAction.Added;
        } else {
          this.appliedCouponCode = '';
          this.appliedCouponName = '';
          this.appliedCoupon = null;
          this.currentState = CouponAction.Collapsed;
          this.mode = this.subscriptionId ? CouponCodeMode.Apply : CouponCodeMode.Input;
          this.reset();
        }
      });
  }

  get activeControl(): FormControl {
    return this.control || this.couponCodeControl;
  }

  get isApplyMode(): boolean {
    return this.mode === CouponCodeMode.Apply;
  }

  get isAppliedMode(): boolean {
    return this.mode === CouponCodeMode.Applied;
  }

  get isInputMode(): boolean {
    return this.mode === CouponCodeMode.Input;
  }

  get isCollapsed(): boolean {
    return this.currentState === CouponAction.Collapsed;
  }

  get isExpanded(): boolean {
    return this.currentState === CouponAction.Expanded;
  }

  get isAdded(): boolean {
    return this.currentState === CouponAction.Added;
  }

  get isDetailedLayout(): boolean {
    return this.displayStyle === CouponDisplayStyle.Detailed;
  }

  get isDefaultLayout(): boolean {
    return this.displayStyle === CouponDisplayStyle.Default;
  }

  get couponDescription(): string {
    if (this.appliedCoupon) {
      return this.getCouponDisplayText(this.appliedCoupon);
    }
    return '';
  }

  private getCouponDisplayText(coupon: any): string {
    if (!coupon) {
      return '';
    }

    let discountText = '';
    if (coupon.percentOff) {
      discountText = `${coupon.percentOff}% off`;
    } else if (coupon.amountOff) {
      discountText = `$${coupon.amountOff} off`;
    }

    if (coupon.duration === 'repeating' && coupon.durationInMonths) {
      return `${discountText} for ${coupon.durationInMonths} ${coupon.durationInMonths === 1 ? 'month' : 'months'}`;
    } else if (coupon.duration === 'once') {
      return `${discountText} for 1 month`;
    } else if (coupon.duration === 'forever') {
      return `${discountText} forever`;
    }

    return discountText;
  }

  toggleExpand() {
    if (this.isCollapsed) {
      this.currentState = CouponAction.Expanded;
    } else if (this.isExpanded) {
      this.currentState = CouponAction.Collapsed;
      this.reset();
    }
  }

  applyCoupon() {
    if (this.inProgress) {
      return;
    }

    this.activeControl.markAsTouched();
    this.activeControl.markAsDirty();

    if (this.activeControl.invalid || !this.activeControl.value) {
      return;
    }

    this.inProgress = true;
    this.errorMessage = '';

    const couponCode = this.activeControl.value;

    // Get the selected plan (from modal selection, not current subscription)
    const selectedPlan = this.subscriptionService.selectedPlan;
    const selectedInterval = this.subscriptionService.selectedInterval;

    // Get the price ID for the selected interval
    const licensedPrice = selectedPlan?.prices?.find(price =>
      price.usageType === 'licensed' && price.interval === selectedInterval
    );
    const priceId = licensedPrice?.id;

    this.subscriptionService.validateCoupon(this.subscriptionId, couponCode, priceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const coupon = response || null;
          this.appliedCoupon = coupon;
          this.appliedCouponCode = coupon?.code || coupon?.id || couponCode;
          this.appliedCouponName = coupon?.name || coupon?.id || couponCode;
          this.currentState = CouponAction.Added;
          this.mode = CouponCodeMode.Applied;
          this.inProgress = false;

          if (coupon?.preview && selectedPlan && (this.subscriptionService.isTrialing || this.subscriptionService.isDowngrade || this.subscriptionService.isUpgrade)) {
            this.subscriptionService.updatePlanWithPreview(selectedPlan, coupon);
          }

          // Emit coupon change to update selected plan display
          this.subscriptionService.couponChanged.next(couponCode);
          this.onCouponApplied.emit();
        },
        error: (error) => {
          this.errorMessage = (typeof error === 'string' ? error : null) || 'Invalid Code';
          this.inProgress = false;
        }
      });
  }

  removeCoupon() {
    if (this.inProgress) {
      return;
    }

    // Just clear the state locally without calling the API
    this.subscriptionService.appliedCoupon = null;
    this.appliedCouponCode = '';
    this.appliedCouponName = '';
    this.appliedCoupon = null;
    this.currentState = CouponAction.Collapsed;
    this.mode = this.subscriptionId ? CouponCodeMode.Apply : CouponCodeMode.Input;
    this.reset();

    // Emit coupon change to update prices across app
    this.subscriptionService.couponChanged.next('');
    this.onCouponRemoved.emit();
  }


  private reset() {
    if (!this.control) {
      this.couponCodeControl.reset();
    } else {
      this.activeControl.reset();
    }
    this.errorMessage = '';
  }
}
